import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TransactionItem } from '@/components/TransactionItem';
import { EmptyState } from '@/components/EmptyState';
import { ArrowLeftRight, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import type { Transaction, Category } from '@/lib/api';
import { fromDateString } from '@/lib/format';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export interface TransactionListProps {
  transactions: Transaction[];
  categories: Category[];
  isLoading: boolean;
  pageSize: number;
  currentPage: number;
  totalItems: number;
  totalPages: number;
  onPageSizeChange: (size: number) => void;
  onPageChange: (page: number) => void;
  onAddTransaction: () => void;
}

export const TransactionList: React.FC<TransactionListProps> = ({
  transactions,
  categories,
  isLoading,
  pageSize,
  currentPage,
  totalItems,
  totalPages,
  onPageSizeChange,
  onPageChange,
  onAddTransaction,
}) => {
  const getCategoryById = (id: string) => categories.find((c) => c.id === id);

  const sortedTransactions = [...transactions].sort((a, b) => {
    const dateA = fromDateString(a.date)?.getTime() ?? 0;
    const dateB = fromDateString(b.date)?.getTime() ?? 0;
    return dateB - dateA;
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-base">Todas as Transações</CardTitle>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="hidden sm:inline text-sm text-muted-foreground">Exibir</span>
          <Select
            value={String(pageSize)}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger className="w-16 h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="hidden sm:inline text-sm text-muted-foreground">por página</span>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : sortedTransactions.length === 0 ? (
          <EmptyState
            icon={ArrowLeftRight}
            title="Nenhuma transação encontrada"
            description="Adicione transações ou ajuste os filtros para ver resultados."
            action={{
              label: 'Nova Transação',
              onClick: onAddTransaction,
            }}
          />
        ) : (
          <>
            <div className="space-y-1">
              {sortedTransactions.map((transaction) => (
                <TransactionItem
                  key={transaction.id}
                  transaction={transaction}
                  category={getCategoryById(transaction.category_id)}
                  categories={categories}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-5 border-t border-border/50 mt-5">
                <p className="hidden sm:block text-sm text-muted-foreground">
                  Exibindo {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, totalItems)} de {totalItems}
                </p>
                <div className="flex items-center gap-1 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="h-8 px-2"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline ml-1">Anterior</span>
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? 'default' : 'outline'}
                          size="sm"
                          className="w-8 h-8 p-0 text-xs"
                          onClick={() => onPageChange(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="h-8 px-2"
                  >
                    <span className="hidden sm:inline mr-1">Próxima</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
